// client/src/pages/HomePage.jsx - Page d'accueil améliorée
import React, { useContext } from 'react';
import { Link, Navigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import Seo from '../components/Seo';

const HomePage = () => {
  const { user, loading } = useContext(AuthContext);

  // Redirige vers le dashboard si l'utilisateur est connecté
  if (loading) return null; // évite un clignotement pendant le chargement
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <>
      <Seo
        title="YAQAM - Idées de recettes intelligentes et personnalisées. Ne soyez plus à cours d'idées de repas de la semaine."
        description="Idées de recettes ultra-personnalisées. Gagnez du temps et de l'inspiration en cuisine grâce à YAQAM. Découvrez des recettes adaptées à vos goûts, vos objectifs nutritionnels et votre budget. Essayez YAQAM gratuitement !"
        keywords="Idées de recettes, idées de repas, idées recettes, planification de repas, recettes personnalisées, repas équilibrés, idées de repas, anti-gaspillage"
        canonical="https://www.yaqam.com/"
        image="/img/idees_repas_front_cover.jpg"
        organization
        breadcrumb
        jsonLd={[
    // 1) WebSite + SearchAction (améliore le sitelinks search box)
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "YAQAM",
      "url": "https://www.yaqam.com/",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://www.yaquoiamanger.com/recherche?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    // 2) WebPage (page d'accueil)
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "YAQAM – Idées de repas personnalisées",
      "url": "https://www.yaquoiamanger.com/",
      "description": "Idées de recettes ultra-personnalisées. Gagnez du temps et de l'inspiration en cuisine grâce à YAQAM.",
      "isPartOf": {
        "@type": "WebSite",
        "name": "YAQAM",
        "url": "https://www.yaquoiamanger.com/"
      }
    }
    ,
    // 3) Organization (site owner)
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "YAQAM",
      "url": "https://www.yaqam.com/",
      // "sameAs": [
      //   "https://www.facebook.com/yaqam",
      //   "https://www.instagram.com/yaqam",
      //   "https://www.linkedin.com/company/yaqam"
      // ],
      "logo": "https://www.yaqam.com/img/logo_color-min.png",
      "contactPoint": [{
        "@type": "ContactPoint",
        "contactType": "customer support",
        "email": "info@j7lab.com",
        //"url": "https://www.yaqam.com/contact"
      }]
    },
    // 4) BreadcrumbList (top-level navigation)
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Accueil",
          "item": "https://www.yaqam.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Idées de recettes",
          "item": "https://www.yaqam.com/idees-de-repas"
        }
      ]
    }
  ]}
      />

      {/* Hero Section Full Cover */}
      <section
        className="relative min-h-screen flex items-center justify-center w-full"
        style={{
          backgroundImage: `url('/img/idees_repas_front_cover.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
        aria-label="Introduction: Génère des idées de repas personnalisées en un clic"
      >
        {/* Overlay pour meilleure lisibilité du texte */}
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        
        {/* Contenu centré */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-extrabold leading-tight tracking-tight mb-8 drop-shadow-2xl">
            Y A Quoi A Manger ? <br />
          </h1>
          <h2 className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto opacity-95 drop-shadow-lg">
            Ne passer plus des heures à chercher des idées de repas.
          </h2>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/register"
              className="px-10 py-4 text-white rounded-full font-bold text-lg shadow-2xl hover:opacity-90 focus:outline-none focus:ring-4 focus:ring-white focus:ring-offset-2 transition-all transform hover:scale-105"
              style={{ backgroundColor: '#E67E22' }}
            >
              Commencer gratuitement
            </Link>
            <Link
              to="/login"
              className="px-10 py-4 bg-white/20 backdrop-blur-sm text-white border-2 border-white rounded-full font-bold text-lg shadow-2xl hover:bg-white/30 focus:outline-none focus:ring-4 focus:ring-white focus:ring-offset-2 transition-all transform hover:scale-105"
            >
              Se connecter
            </Link>
          </div>
        </div>

        {/* Indicateur de scroll */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>
      {/* Problem/Solution Section */}
      <section className="py-20 bg-white" aria-label="Problèmes & solutions YAQAM">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Problem Statement */}
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-extrabold mb-8" style={{ color: '#2C3E50' }}>
              La fin du casse-tête des repas au quotidien
            </h3>
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              
              {/* Problème 1 */}
              <div className="flex flex-col items-center p-6">
                <div className="w-32 h-32 rounded-full flex items-center justify-center mb-4 overflow-hidden" style={{ backgroundColor: '#e74c3c20' }}>
                  <img
                    src="/img/find_recipes_ideas.png"
                    alt="Stress quotidien"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
                <h4 className="text-lg font-semibold mb-3" style={{ color: '#2C3E50' }}>Stress quotidien</h4>
                <p className="text-center" style={{ color: '#6c757d' }}>
                  <strong>Qu'est-ce qu'on mange ce soir ?</strong> Cette question revient chaque jour et vous épuise.
                </p>
              </div>

              {/* Problème 2 */}
              <div className="flex flex-col items-center p-6">
                <div className="w-32 h-32 rounded-full flex items-center justify-center mb-4 overflow-hidden" style={{ backgroundColor: '#f39c1220' }}>
                  <img
                    src="/img/need_cooking_ideas.png"
                    alt="Routine alimentaire"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
                <h4 className="text-lg font-semibold mb-3" style={{ color: '#2C3E50' }}>Routine alimentaire</h4>
                <p className="text-center" style={{ color: '#6c757d' }}>
                  Toujours les mêmes plats, par manque d’inspiration et de temps. Besoin de nouvelles <strong>idées de repas</strong>.
                </p>
              </div>

              {/* Problème 3 */}
              <div className="flex flex-col items-center p-6">
                <div className="w-32 h-32 rounded-full flex items-center justify-center mb-4 overflow-hidden" style={{ backgroundColor: '#e74c3c20' }}>
                  <img
                    src="/img/no_fast_food.png"
                    alt="Solutions de facilité"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
                <h4 className="text-lg font-semibold mb-3" style={{ color: '#2C3E50' }}>Solutions de facilité</h4>
                <p className="text-center" style={{ color: '#6c757d' }}>
                  Fast-food, plats préparés… des choix rapides mais chers et pas bons. Vous avez envie de cuisiner mais <strong>vous ne savez pas quoi faire à manger...</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Solution Statement - with cover background */}
          <div className="relative text-center py-16 px-8 rounded-2xl overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url('/img/idees_repas_personnalisees.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative z-10">
              <h3 className="text-3xl md:text-4xl font-extrabold mb-8 text-white drop-shadow">
                <span style={{ color: '#E67E22' }}>YAQAM</span> génères des <strong>idées de repas</strong> <span style={{ color: '#E67E22' }}>qui vous correspondent.</span>
              </h3>
              <p className="text-xl mb-8 max-w-4xl mx-auto text-white/90">
                <strong>Des idées de repas</strong> <span style={{ color: '#E67E22' }}>adaptés votre profil, vos envies et vos objectifs en quelques clics</span>. 
              </p>
              <div className="grid md:grid-cols-3 gap-8">
              
              {/* Solution 1 */}
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <span className="text-3xl block mb-4">✨</span>
                <h4 className="font-semibold mb-2" style={{ color: '#E67E22' }}>Moins de stress</h4>
                <p style={{ color: '#6c757d' }}>Des <strong>idées de repas</strong> quotidiennes, adaptées à vos contraintes de temps et budget.</p>
              </div>

              {/* Solution 2 */}
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <span className="text-3xl block mb-4">🥗</span>
                <h4 className="font-semibold mb-2" style={{ color: '#E67E22' }}>Variez vos repas</h4>
                <p style={{ color: '#6c757d' }}>Génère des <strong>idées de recettes</strong> personnalisées qui cassent la routine et s’adaptent à votre frigo.</p>
              </div>

              {/* Solution 3 */}
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <span className="text-3xl block mb-4">💚</span>
                <h4 className="font-semibold mb-2" style={{ color: '#E67E22' }}>Manger mieux</h4>
                <p style={{ color: '#6c757d' }}>Des <strong>idées de repas équilibrés</strong>, savoureux et faits maison, loin des plats préparés.</p>
              </div>

              </div>
              <p className="text-xl mb-8 max-w-4xl mx-auto text-white/90" style={{ color: '#E67E22' }}>Fini le stress, place au plaisir de cuisiner.</p>
            </div>
          </div>
        </div>
      </section>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Features Section */}
        <section className="py-20 rounded-2xl" style={{ backgroundColor: '#f8f9fa' }} aria-label="Fonctionnalités de YAQAM">
          <h3 className="text-3xl md:text-4xl font-extrabold text-center mb-16" style={{ color: '#2C3E50' }}>
            Comment fonctionne YAQAM ?
          </h3>
          <div className="grid md:grid-cols-3 gap-10">
            {/* Feature 1 */}
            <article className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2" aria-label="Idées de repas personnalisées">
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-6 shadow-lg mx-auto"
             style={{ backgroundColor: '#2C3E50' }}>
          1
              </div>
              <h4 className="text-xl font-semibold mb-4 text-center" style={{ color: '#2C3E50' }}>
          YAQAM <span style={{ color: '#E67E22' }}>apprend</span> vos goûts
              </h4>
              <p className="text-center" style={{ color: '#6c757d' }}>
          Notre algorithme ajuste vos menus au fil du temps pour coller parfaitement à <strong>votre style de vie</strong>, vos <strong>objectifs nutritionnels</strong> et <strong>votre matériel de cuisine</strong>.
              </p>
            </article>

            {/* Feature 2 */}
            <article className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2" aria-label="Anti-gaspillage">
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-6 shadow-lg mx-auto"
             style={{ backgroundColor: '#2C3E50' }}>
          2
              </div>
              <h4 className="text-xl font-semibold mb-4 text-center" style={{ color: '#2C3E50' }}>
          YAQAM génère <span style={{ color: '#E67E22' }}>des idées de recettes anti-gaspi </span>avec ce que vous avez dans le frigo
              </h4>
              <p className="text-center" style={{ color: '#6c757d' }}>
          Ajoutez les ingrédients de votre frigo ou vos restes, et <strong>YAQAM revisite les recettes</strong> pour limiter le gaspillage tout en restant savoureuse.
              </p>
            </article>

            {/* Feature 3 */}
            <article className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2" aria-label="Recettes créatives">
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-6 shadow-lg mx-auto"
             style={{ backgroundColor: '#2C3E50' }}>
          3
              </div>
              <h3 className="text-xl font-semibold mb-4 text-center" style={{ color: '#2C3E50' }}>
          Des idées de repas originales, <span style={{ color: '#E67E22' }}>auxquelles vous n'auriez jamais</span> imaginées
              </h3>
              <p className="text-center" style={{ color: '#6c757d' }}>
          Découvrez chaque semaine de nouvelles associations d'ingrédients et des saveurs inspirées du monde entier pour casser la routine.
              </p>
            </article>
          </div>
        </section>

        {/* Dernières idées de recettes générées */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white" aria-label="Dernières idées de recettes générées">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-6" style={{ color: '#2C3E50' }}>
              Dernières idées de recettes que YAQAM a généré pour nos utilisateurs
            </h2>
            <p className="text-center mb-16 max-w-3xl mx-auto text-lg" style={{ color: '#6c757d' }}>
              Découvrez les créations culinaires les plus récentes créé par YAQAM, adaptées aux goûts et préférences de notre communauté.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Exemple de recette 1 */}
              <article className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2 overflow-hidden">
                <div className="h-56 overflow-hidden">
                  <img
                    src="/img/travel_cooking.png"
                    alt="Pasta aux champignons et parmesan"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#2C3E50' }}>
                    Pasta aux champignons et parmesan
                  </h3>
                  <p className="text-sm mb-4" style={{ color: '#6c757d' }}>
                    Une recette réconfortante générée pour spécialement pour Marie, amatrice de cuisine italienne végétarienne.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#87A96B', color: 'white' }}>
                      Végétarien
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#2C3E50', color: 'white' }}>
                      Cuisine du monde
                    </span>
                    <span className="text-xs" style={{ color: '#6c757d' }}>
                      ⏱️ 25 min
                    </span>
                  </div>
                </div>
              </article>

              {/* Exemple de recette 2 - Centré */}
              <article className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2 overflow-hidden">
                <div className="h-56 overflow-hidden">
                  <img
                    src="/img/idees_recettes_aperitif_dinatoire.png"
                    alt="Tarte frittata aux épinards et cèpes"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#2C3E50' }}>
                    Tarte frittata aux épinards et cèpes
                  </h3>
                  <p className="text-sm mb-4" style={{ color: '#6c757d' }}>
                    Créé pour Christelle qui cherchait des idées d'apéritif dînatoire avec ses amis du boulot.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#E67E22', color: 'white' }}>
                      Apéritif
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#87A96B', color: 'white' }}>
                      Convivial
                    </span>
                    <span className="text-xs" style={{ color: '#6c757d' }}>
                      ⏱️ 15 min
                    </span>
                  </div>
                </div>
              </article>

              {/* Exemple de recette 3 */}
              <article className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2 overflow-hidden">
                <div className="h-56 overflow-hidden">
                  <img
                    src="/img/recettes_sport_performance.png"
                    alt="Salade de quinoa et dés de feta, aux légumes grillés"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#2C3E50' }}>
                    Salade de quinoa et dés de feta, aux légumes grillés
                  </h3>
                  <p className="text-sm mb-4" style={{ color: '#6c757d' }}>
                    Créée pour Thomas, sportif cherchant des <strong>idées de repas</strong> simples, rapides à cuisiner, riches en protéines et légers.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#E67E22', color: 'white' }}>
                      Sportif
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#2C3E50', color: 'white' }}>
                      Facile & Rapide
                    </span>
                    <span className="text-xs" style={{ color: '#6c757d' }}>
                      ⏱️ 20 min
                    </span>
                  </div>
                </div>
              </article>
            </div>

            <div className="text-center mt-12">
              <Link
                to="/idees-de-repas"
                className="inline-block px-8 py-3 text-white rounded-full font-semibold shadow-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all transform hover:scale-105"
                style={{ backgroundColor: '#87A96B', color: '#ffffff', textDecoration: 'none' }}
              >
                Voir toutes les dernières idées de recettes
              </Link>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20" aria-label="Tarifs de YAQAM">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-16" style={{ color: '#2C3E50' }}>
            Nos offres
          </h2>
          <div className="flex flex-col md:flex-row gap-10 justify-center">
            {/* Plan Gratuit */}
            <article className="bg-white p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2 max-w-md w-full">
              <h3 className="text-2xl font-bold mb-4 text-center" style={{ color: '#2C3E50' }}>Gratuit</h3>
              <p className="text-5xl font-extrabold text-center mb-8" style={{ color: '#87A96B' }}>0€</p>
              <ul className="space-y-3 mb-10" style={{ color: '#6c757d' }}>
                {[
                  '12 suggestions par semaine',
                  '3 recettes par semaine',
                  '3 itérations de recettes',
                  '3 questions à YAQAM',
                  'Recettes publiques',
                ].map((item) => (
                  <li key={item} className="flex items-start">
                    <span className="mr-3" style={{ color: '#87A96B' }}>✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="block w-full px-8 py-3 text-white rounded-full font-semibold text-center shadow-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all transform hover:scale-105"
                style={{ backgroundColor: '#2C3E50' }}
              >
                Commencer gratuitement
              </Link>
            </article>

            {/* Plan Premium */}
            <article className="relative p-10 rounded-2xl shadow-2xl max-w-md w-full border-2 hover:shadow-3xl transition-all transform hover:-translate-y-2"
                     style={{ backgroundColor: '#f8f9fa', borderColor: '#E67E22' }}>
              <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-white px-5 py-1 rounded-full text-sm font-medium shadow-lg"
                    style={{ backgroundColor: '#E67E22' }}>
                Recommandé
              </span>
              <h3 className="text-2xl font-bold mb-4 text-center" style={{ color: '#2C3E50' }}>Premium</h3>
              <p className="text-5xl font-extrabold text-center mb-8" style={{ color: '#E67E22' }}>
                9,99€<span className="text-lg font-normal" style={{ color: '#6c757d' }}> /mois</span>
              </p>
              <ul className="space-y-3 mb-10" style={{ color: '#6c757d' }}>
                {[
                  '120 suggestions par semaine',
                  '30 recettes par semaine',
                  '30 itérations de recettes',
                  '30 questions à YAQAM',
                  'Recettes privées',
                ].map((item) => (
                  <li key={item} className="flex items-start">
                    <span className="mr-3" style={{ color: '#E67E22' }}>✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="block w-full px-8 py-3 text-white rounded-full font-semibold text-center shadow-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all transform hover:scale-105"
                style={{ backgroundColor: '#E67E22' }}
              >
                Essayer Premium
              </Link>
            </article>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20" aria-label="Témoignages utilisateurs">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-16" style={{ color: '#2C3E50' }}>
            Ils en parlent mieux que nous
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {/* Témoignage */}
            {[{
              quote: 'Fini le stress des menus ! YAQAM a révolutionné nos repas. Il me propose en 3 clics des menus qui plaisent à mes enfants. Ils en redemandent.',
              name: 'Marine C.',
            },
            {
              quote: 'Sportif, je fais les recettes YAQAM sans prise de tête et sans penser à ma ligne. J\'ai perdu du poids et augmenté mes performances !',
              name: 'Jérémie A.',
            },
            {
              quote: 'Je cuisine végé et je découvre chaque semaine de nouvelles idées équilibrées et rapides.',
              name: 'Corentin J.',
            }].map(({ quote, name }) => (
              <figure key={name} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2">
                <div className="flex items-center mb-4" style={{ color: '#E67E22' }} aria-hidden="true">
                  {'★★★★★'}
                </div>
                <blockquote className="mb-6" style={{ color: '#6c757d' }}>"{quote}"</blockquote>
                <figcaption className="font-medium" style={{ color: '#2C3E50' }}>{name}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20 text-white rounded-2xl text-center" 
                 style={{ background: 'linear-gradient(135deg, #2C3E50 0%, #34495e 100%)' }}
                 aria-label="Appel à l'inscription">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-6">
            Prêt·e à révolutionner vos repas ?
          </h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto opacity-90">
            Rejoignez des milliers d'utilisateurs qui gagnent du temps, économisent de l'argent et mangent de manière équilibrée grâce à YAQAM.
          </p>
          <Link
            to="/register"
            className="inline-block px-10 py-4 text-white rounded-full font-extrabold text-lg shadow-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800 transition-all transform hover:scale-105"
            style={{ backgroundColor: '#E67E22' }}
          >
            Commencer maintenant
          </Link>
        </section>
      </main>
    </>
  );
};

export default HomePage;
